import { downloadComments } from '@api/comment';
import ImportantNotificationBanner from '@components/ImportantNotificationBanner';
import SuccessNotificationBanner from '@components/SuccessNotificationBanner';
import React, { useState } from 'react';

interface CaseNotesProps {
  fullName: string;
  slug: string | undefined;
}

const CaseNotes: React.FC<CaseNotesProps> = ({ fullName, slug }) => {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadSuccess, setDownloadSuccess] = useState(false);

  const handleDownloadPDF = async () => {
    try {
      if (!slug) return;

      setIsDownloading(true);
      setDownloadSuccess(false);

      // Enforce a minimum 2–3 second loading duration
      const MIN_LOADING_TIME = 2000; // 2 seconds

      // Promise that resolves after the delay
      const delay = new Promise(resolve => {
        setTimeout(resolve, MIN_LOADING_TIME);
      });

      // Run the API call and delay in parallel
      const [response] = await Promise.all([downloadComments(slug), delay]);

      if (!response) {
        throw new Error('No data received from server');
      }

      // Convert response into a blob
      const url = window.URL.createObjectURL(new Blob([response]));

      // Create a temporary download link
      const link = document.createElement('a');
      link.href = url;
      link.download = `${fullName}-patient-notes.zip`;
      document.body.appendChild(link);
      link.click();

      // Cleanup
      link.remove();
      window.URL.revokeObjectURL(url);

      setErrorMessage(null);
      setDownloadSuccess(true);
    } catch (error: any) {
      console.error('Download failed:', error);
      setErrorMessage(
        error?.message ||
          'Something went wrong while downloading the PDF. Please try again.'
      );
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div>
      <div className="flex justify-between mt-10 govuk-bp:mt-14 mb-3">
        <h1 className="govuk-heading-l">{fullName} - Patient Notes</h1>
        {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
        <h3
          className="govuk-heading-s underline"
          style={{ cursor: 'pointer' }}
          onClick={handleDownloadPDF}
        >
          Download case notes
        </h3>
      </div>
      <h3 className="govuk-heading-s">
        Patient Notes and safeguarding concerns are not shared externally.
      </h3>
      {errorMessage && (
        <div
          className="govuk-error-message"
          style={{ marginTop: '1rem', color: 'red' }}
        >
          {errorMessage}
        </div>
      )}
      {isDownloading && (
        <ImportantNotificationBanner
          title=" Patient Notes are downloading."
          duration={10000}
          noScrollToTop
        />
      )}
      {downloadSuccess && !isDownloading && (
        <SuccessNotificationBanner
          title="Patient Notes have have been downloaded"
          duration={10000}
          noScrollToTop
        />
      )}
    </div>
  );
};

export default CaseNotes;
